import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { InputType, XumoForm, StoryCard } from 'ui';
import { XumoButton as Button } from 'ui';
import _ from 'lodash';
import { SingleImageBlock } from './blocks';
import { createPressSlug } from 'config/utils/createPressSlug';

const PressForm: React.FC<{ sidebar_form: string }> = ({ sidebar_form }) => {
  let formData = {};
  const advertisingData = {
    heading: '<h3>Partner <strong>with us</strong></h3>',
    body: '<p>Connect with our team to learn about how you can leverage Xumo’s inventory to meet new audiences and reach your goals.</p>',
    formFields: [
      {
        type: InputType.Name,
        name: 'first_name',
        placeholder: 'First name',
        width: 'full',
      },
      {
        type: InputType.Name,
        name: 'last_name',
        placeholder: 'Last name',
        width: 'full',
      },
      {
        type: InputType.Text,
        name: 'job_title',
        placeholder: 'Job title',
        width: 'full',
      },
      {
        type: InputType.Email,
        name: 'email',
        placeholder: 'Email',
        width: 'full',
      },
      {
        type: InputType.Name,
        name: 'company',
        placeholder: 'Company or agency name',
        width: 'full',
      },
      {
        type: InputType.Text,
        name: 'company_type',
        placeholder: 'Company type',
        width: 'full',
      },
      {
        type: InputType.Url,
        name: 'website_url',
        placeholder: 'Website URL',
        width: 'full',
      },
      {
        type: InputType.Text,
        name: 'representation',
        placeholder: 'If agency, what advertiser(s) do you represent?',
        width: 'full',
      },
      {
        type: InputType.Textarea,
        name: 'message',
        placeholder: 'How can we help with your next advertising campaign? ',
        width: 'full',
      },
    ],
    formAction: {
      submissionEndpoint: '/api/contact',
      successMessage: '',
      formType: 'advertising',
    },
  };
  const businessData = {
    heading: '<h3>Get started <strong>today</strong></h3>',
    body: '<p>Connect with our team to learn how you can leverage Xumo for your unique FAST needs.</p>',
    formFields: [
      {
        type: InputType.Name,
        name: 'first_name',
        placeholder: 'First name',
        width: 'full',
      },
      {
        type: InputType.Name,
        name: 'last_name',
        placeholder: 'Last name',
        width: 'full',
      },
      {
        type: InputType.Email,
        name: 'email',
        placeholder: 'Email',
        width: 'full',
      },
      {
        type: InputType.Name,
        name: 'company',
        placeholder: 'Company',
        width: 'full',
      },
      {
        type: InputType.Textarea,
        name: 'message',
        placeholder: 'Message',
        width: 'full',
      },
    ],
    formAction: {
      submissionEndpoint: '/api/contact',
      successMessage: '',
      formType: 'business',
    },
  };
  const prospectData = {
    heading: '<h3>Level up <strong>your inbox</strong></h3>',
    body: '<p>Sign up for emails from Xumo to keep up with all the latest shows and movies, new products and exclusive offers.</p>',
    formFields: [
      { name: 'first_name', type: InputType.Text, placeholder: 'First Name', width: 'full' },
      { name: 'last_name', type: InputType.Text, placeholder: 'Last Name', width: 'full' },
      { name: 'email', type: InputType.Email, placeholder: 'Email', width: 'full' },
    ],
    formAction: {
      submissionEndpoint: '/api/prospect',
      successTitle: 'All set',
      successMessage: 'We’ve received your info — our emails will be landing in your inbox soon.',
      errorMessage: 'Unable to subscribe',
    },
  };

  switch (sidebar_form) {
    case 'advertising':
      formData = advertisingData;
      break;
    case 'business':
      formData = businessData;
      break;
    default:
      formData = prospectData; //prospect is fallback
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-3">
      <h2 className="type-h3-press" data-i18n={formData.heading} dangerouslySetInnerHTML={{ __html: formData.heading }} />
      <div className="type-small-body-press" data-i18n={formData.body} dangerouslySetInnerHTML={{ __html: formData.body }} />
      <div className="mt-3 w-full">
        <XumoForm
          formFields={formData.formFields}
          CTA={{
            label: 'Submit',
            href: ' ',
          }}
          formAction={formData.formAction}
          styleOverrides={{
            CTA: {
              general: `w-full text-white border-none mt-5`,
              disabledColor: 'bg-xumoAltBerryDark',
              enabledColor: 'bg-xumoAltBerryLight',
              alignment: '',
              padding: 'py-3 px-8',
              border_radius: 'rounded',
            },
            formField: `w-full rounded-none outline-1 outline-[#C2C2C2] placeholder:text-black`,
            hideLabel: true,
            form: 'w-full justify-center items-center gap-2',
            accentColor: 'xumoAltBerryLight',
            hoverColor: 'xumoAltBerryDark',
          }}
        />
      </div>
    </div>
  );
};

export const PressSidebar: React.FC<{
  sidebar_similar_content: any;
  sidebar_featured_resource: any;
  sidebar_featured_resource_image: any;
  sidebar_form: string;
}> = ({ sidebar_similar_content, sidebar_featured_resource, sidebar_featured_resource_image, sidebar_form }) => {
  const parentVariants = {
    initial: {
      opacity: 1,
    },
    animate: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const childVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        damping: 65,
        stiffness: 900,
        mass: 1,
      },
    },
  };

  const sideBarSectionClass = 'w-full flex flex-col items-start justify-start gap-3';
  const sidebarUnderlineClass = 'border-CCC6C6 border-b-2 pb-12';

  return (
    <motion.div
      className="flex h-full w-full flex-col items-start justify-start gap-10 lg:max-w-[350px] xl:max-w-[400px]"
      initial="initial"
      animate="animate"
      key="press-sidebar"
      variants={parentVariants}
    >
      {/* Similar Content */}
      {sidebar_similar_content.length > 0 && (
        <div className={`${sideBarSectionClass} ${sidebarUnderlineClass} gap-6`}>
          {sidebar_similar_content.map((item: any, id: number) => {
            const postURL = item?.title ? createPressSlug(item.title) : undefined;
            return (
              <motion.div variants={childVariants}>
                <StoryCard key={item.title + id} story={item} postURL={`/${postURL}`} pressSidebar={true} fullpage />
              </motion.div>
            );
          })}
          <motion.div variants={childVariants} className="h-full w-full px-[20px]">
            <Button
              label="View all news"
              href="/press"
              light={false}
              primary={true}
              color="xumoAltBerryLight"
              padding="py-3 px-8"
              border_radius="rounded"
              className="mt-5 w-full border-none text-center"
            />
          </motion.div>
        </div>
      )}
      {/* Featured Resources */}
      {sidebar_featured_resource && (
        <motion.div variants={childVariants} className={`${sideBarSectionClass} ${sidebarUnderlineClass} px-[20px]`}>
          <h2 className="type-small-heading-press">Featured Resources</h2>
          <div className="flex h-full w-full flex-col gap-2">
            <SingleImageBlock image={sidebar_featured_resource_image} />
            <h3 data-i18n className="type-h3-press" dangerouslySetInnerHTML={{ __html: sidebar_featured_resource.title }} />
            <div data-i18n className="type-small-body-press" dangerouslySetInnerHTML={{ __html: sidebar_featured_resource.description }} />
          </div>
          <Button
            label="Download Resource"
            target="_blank"
            href={`https://${process.env.DIRECTUS_APP_ID}.directus.app/assets/${sidebar_featured_resource.filename_disk}`}
            light={false}
            primary={true}
            className="mt-5 w-full border-none text-center"
            color="xumoAltBerryLight"
            padding="py-3 px-8"
            border_radius="rounded"
          />
        </motion.div>
      )}
      {sidebar_form && (
        <motion.div variants={childVariants} className={`${sideBarSectionClass} px-[20px]`}>
          <PressForm sidebar_form={sidebar_form} />
        </motion.div>
      )}
    </motion.div>
  );
};
